/**
 * Gera um schema Zod **dinâmico** a partir de um `FormSchema` (shape
 * do template). Usado no submit final para validar respostas antes de
 * enviar ao backend.
 *
 * Regras:
 *   - Campos `required=true` exigem valor não-vazio (string com
 *     `length > 0` ou array com `length > 0`).
 *   - Campos `required=false` aceitam `undefined`, `null` ou string vazia.
 *   - `email` → regex simples de email válido.
 *   - `cnpj` → 14 dígitos após normalização (aceita máscara no input).
 *   - `cep` → 8 dígitos após normalização.
 *   - `tel` → 10 ou 11 dígitos (telefone BR com DDD).
 *   - `select`/`radio` → valor deve estar na lista de `options[].value`.
 *
 * **Importante**: não valida `visible_if`. Se um campo está
 * escondido pela lógica condicional, o renderer **remove** sua
 * resposta do objeto antes de passar pra `validateAnswers`. Assim
 * um campo oculto required não trava o submit.
 */
import { z } from 'zod';

import type { FormField, FormSchema } from '../../schemas.js';
import { isVisible } from './visibleIf.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function buildFieldValidator(field: FormField): z.ZodTypeAny {
  const required = field.required;

  // Base por tipo.
  let base: z.ZodTypeAny;

  switch (field.type) {
    case 'text':
    case 'textarea':
      base = z.string();
      if (field.maxlength) base = (base as z.ZodString).max(field.maxlength);
      break;

    case 'email':
      base = z.string().regex(EMAIL_RE, 'Email inválido.');
      break;

    case 'tel': {
      const digitsOnly = z
        .string()
        .transform((v) => v.replace(/\D/g, ''))
        .refine((v) => v.length === 10 || v.length === 11, 'Telefone deve ter 10 ou 11 dígitos.');
      base = digitsOnly;
      break;
    }

    case 'cnpj': {
      base = z
        .string()
        .transform((v) => v.replace(/\D/g, ''))
        .refine((v) => v.length === 14, 'CNPJ deve ter 14 dígitos.');
      break;
    }

    case 'cep': {
      base = z
        .string()
        .transform((v) => v.replace(/\D/g, ''))
        .refine((v) => v.length === 8, 'CEP deve ter 8 dígitos.');
      break;
    }

    case 'select':
    case 'radio': {
      const allowed = (field.options ?? []).map((o) => o.value);
      if (allowed.length === 0) {
        base = z.string(); // edge: schema corrompido; trata depois
      } else {
        base = z.enum(allowed as [string, ...string[]]);
      }
      break;
    }

    case 'multiselect': {
      const allowed = (field.options ?? []).map((o) => o.value);
      if (allowed.length === 0) {
        base = z.array(z.string());
      } else {
        // Array de enum — cada valor deve estar na lista de options.
        base = z.array(z.enum(allowed as [string, ...string[]]));
      }
      // Required em multiselect = array com ≥ 1 item.
      if (required) {
        base = (base as z.ZodArray<z.ZodString>).min(1, 'Selecione ao menos uma opção.');
        return base;
      }
      // Opcional: aceita undefined/null/array vazio.
      return z.preprocess((v) => (v === null || v === undefined ? undefined : v), base.optional());
    }
  }

  // Required vs optional.
  if (required) {
    // Para string-like, rejeita vazio. Para os transformados (tel/cnpj/cep),
    // o refine já cuida. Para text/textarea, precisamos min(1).
    if (field.type === 'text' || field.type === 'textarea' || field.type === 'email') {
      base = (base as z.ZodString).min(1, 'Campo obrigatório.');
    }
    return base;
  }

  // Opcional: aceita undefined/null/string vazia.
  return z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : v),
    base.optional(),
  );
}

/**
 * Produz um schema Zod para o `answers` inteiro de uma submissão.
 * Chave = `field.id`. Follow-ups e other_fields entram como chaves
 * separadas no mesmo nível.
 *
 * **Ciente de visibilidade**: campos com `visible_if` que avaliam
 * falso (ou filhos `follow_up`/`other_field` cujo pai está oculto
 * com a visible_if desses campos) viram `optional` no validator
 * mesmo sendo `required` no schema. Sem isso, um required escondido
 * bloqueia o submit quando o user escolhe uma ramificação que não
 * precisa dele.
 *
 * Passa `answers` vazio para validar como se tudo estivesse visível
 * (útil em testes e no backend que não tem contexto de UI).
 */
export function buildAnswersValidator(
  schema: FormSchema,
  answers: Record<string, unknown> = {},
): z.ZodTypeAny {
  const shape: Record<string, z.ZodTypeAny> = {};

  const addField = (field: FormField, parentVisible: boolean) => {
    const visible = parentVisible && isVisible(field.visible_if, answers);
    const effective: FormField = visible ? field : { ...field, required: false };
    shape[field.id] = buildFieldValidator(effective);
    if (field.follow_up) addField(field.follow_up, visible);
    if (field.other_field) addField(field.other_field, visible);
  };

  for (const section of schema.sections) {
    for (const field of section.questions) {
      addField(field, true);
    }
  }

  // `passthrough` tolerante para compatibilidade futura — se o schema
  // tiver um campo novo que o validator não conhece, não trava. O
  // backend é a linha final de defesa.
  return z.object(shape).passthrough();
}
